import { isEqual } from 'lodash'
import { _getPDataById, getPersonListData } from '../persons/mockData'
import {
  getSupplierContactRoleListData,
  _getSCRDataById,
} from '../selectLists/supplierContactRoles/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import type { TEntityId } from '~/types/models'
import type { ISupplierContact } from '~/types/contact/models'
import {
  ContactStatusIds,
  getContactStatusById,
} from '@/services/selectLists/contactStatii'
import { ResourceUseIds } from '~/services/selectLists/resourceUses'

const supplierContactListData = undefined as unknown as ISupplierContact[]

export const getSupplierContactListData = getListDataFunctionFactory<
  ISupplierContact[]
>({
  listData: supplierContactListData,
  storageKey: 'suppliercontacts',
  getStartingListData: getStartingSupplierContactListData,
  sortFunction: (a, b) =>
    a.person.name.lastName > b.person.name.lastName ? 1 : -1,
})

export function _getSCTDataById(
  id: TEntityId,
  list: ISupplierContact[]
): ISupplierContact {
  if (!list) {
    throw new Error(`_getSCTDataById called with undefined/null list`)
  }
  const supplierContact = list.find((cct: ISupplierContact) => cct.id === id)
  if (!supplierContact) {
    throw new Error(`Can't find supplierContact '${id}'`)
  }
  return supplierContact
}

export async function getSupplierContactDataById(
  id: TEntityId
): Promise<ISupplierContact> {
  const list: ISupplierContact[] = await getSupplierContactListData()
  return _getSCTDataById(id, list)
}

export async function getSupplierContactListDataBySupplierId(id: string) {
  const list = await getSupplierContactListData()
  return list.filter((supplierContact: ISupplierContact) =>
    isEqual(supplierContact.supplier?.id, id)
  ) as ISupplierContact[]
}

export async function getSupplierContactListDataWithUseForRegistrationBySupplierId(
  id: string
) {
  const list = await getSupplierContactListData()
  return list.filter(
    (supplierContact: ISupplierContact) =>
      isEqual(supplierContact.supplier?.id, id) &&
      supplierContact.useFor?.find(
        (uf) => uf.id === ResourceUseIds.REGISTRATIONS
      )
  ) as ISupplierContact[]
}

export async function getStartingSupplierContactListData(): Promise<
  ISupplierContact[]
> {
  const plist = await getPersonListData()
  const scrList = await getSupplierContactRoleListData()
  return [
    {
      id: 'XXSCT0001',
      hrid: 'SCT0001',
      person: _getPDataById('XXPE0018', plist),
      phone: '(510) 343-3222',
      cell: '(555) 999-8870',
      email: 'mstreep@wxtechnologygroup.com',
      supplier: {
        // NOTE: Can't use get method for supplier or we get circular reference
        id: 'XXS0001',
        label: 'WXTG',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0002',
      hrid: 'SCT0002',
      person: _getPDataById('XXPE0019', plist),
      phone: '(520) 494-4049',
      cell: '(555) 999-8869',
      email: 'james@cogent.com',
      supplier: {
        id: 'XXS0002',
        label: 'Cogent',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      // useFor: [getResourceUseById(ResourceUseIds.REGISTRATIONS)], // for testing
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0003',
      hrid: 'SCT0003',
      person: _getPDataById('XXPE0020', plist),
      phone: '(510) 222-5656',
      cell: '(555) 999-8868',
      email: 'jeffrey@comcast.com',
      supplier: {
        id: 'XXS0003',
        label: 'Comcast',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0004',
      hrid: 'SCT0004',
      person: _getPDataById('XXPE0021', plist),
      phone: '(310) 111-3230',
      cell: '(555) 999-8867',
      email: 'jonathan@nitel.com',
      supplier: {
        id: 'XXS0004',
        label: 'Nitel',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0005',
      hrid: 'SCT0005',
      person: _getPDataById('XXPE0022', plist),
      phone: '(626) 454-1234',
      cell: '(555) 999-8866',
      email: 'lee@ringcentral.com',
      supplier: {
        id: 'XXS0005',
        label: 'RingCentral',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0006',
      hrid: 'SCT0006',
      person: _getPDataById('XXPE0023', plist),
      phone: '(838) 529-1293',
      cell: '(555) 999-8865',
      email: 'robert@genesys.com',
      supplier: {
        id: 'XXS0006',
        label: 'Genesys',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0007',
      hrid: 'SCT0007',
      person: _getPDataById('XXPE0024', plist),
      phone: '(727) 800-1453',
      cell: '(555) 999-8864',
      email: 'faye@8x8.com',
      supplier: {
        id: 'XXS0007',
        label: '8x8',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0008',
      hrid: 'SCT0008',
      person: _getPDataById('XXPE0025', plist),
      phone: '(213) 122-0004',
      cell: '(555) 999-8863',
      email: 'ursuls@five9.com',
      supplier: {
        id: 'XXS0008',
        label: 'Five9',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXSCT0009',
      hrid: 'SCT0009',
      person: _getPDataById('XXPE0032', plist),
      phone: '(510) 222-7878',
      cell: '(555) 999-9898',
      email: 'harlan@pactel.net',
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      role: _getSCRDataById('XXSCR003', scrList),
      status: getContactStatusById(ContactStatusIds.ACTIVE),
      version: '1',
      canBeDeleted: false,
    },
  ]
}
